/**
 * 表单图片：选择后本地预览，点击保存时再上传 OSS/本机。
 * 最后更新：2026-07-14（picture-card 编辑回显支持 OSS 预签）
 */
import {
  attachLocalAvatarFile,
  revokeLocalAvatarPreview,
  uploadHeadPic,
  uploadPic,
  resolveImageSrc,
  stripOssPresignParams,
  getUploadSchoolId
} from '../utils/ossMediaUpload'
import {
  batchResolveOssMediaUrls,
  lookupCachedOssSignedUrl,
  stripOssUrlQuery,
  isAliyunOssHttpsUrl,
  resolveMediaUrl
} from '../utils/mediaUrl'

export default {
  data() {
    return {
      deferredImagePending: {}
    }
  },
  beforeDestroy() {
    this.deferredImageClearAll()
  },
  methods: {
    /** 编辑打开时记录已有服务器路径 */
    deferredImageInit(key, serverUrl) {
      if (!key) return
      this.$set(this.deferredImagePending, key, {
        file: null,
        previewUrl: '',
        savedUrl: serverUrl || ''
      })
    },
    deferredImageDisplay(key, serverUrl) {
      const item = this.deferredImagePending[key]
      if (item && item.previewUrl) return item.previewUrl
      return serverUrl || (item && item.savedUrl) || ''
    },
    deferredImageResolve(key, serverUrl) {
      // 仅用于本机相对路径拼接；OSS 私有桶预览请用 v-oss-src + deferredImageDisplay
      return resolveImageSrc(this.deferredImageDisplay(key, serverUrl), this.BaseApiUrl)
    },
    deferredImageChange(key, file, options) {
      if (!file || !file.raw) return
      const raw = file.raw
      const types = (options && options.acceptTypes) || ['image/jpg', 'image/png', 'image/jpeg']
      if (types.indexOf(raw.type) < 0) {
        this.$message.error((options && options.typeError) || '图片格式错误，请上传 jpg/png')
        return
      }
      this.deferredImageRevoke(key)
      const local = attachLocalAvatarFile(raw)
      this.$set(this.deferredImagePending, key, {
        file: local.pendingAvatarFile,
        previewUrl: local.pendingAvatarPreviewUrl,
        savedUrl: (options && options.savedUrl) || ''
      })
      if (options && typeof options.onPreview === 'function') {
        options.onPreview(local.pendingAvatarPreviewUrl)
      }
    },
    deferredImageRevoke(key) {
      const item = this.deferredImagePending[key]
      if (item && item.previewUrl) revokeLocalAvatarPreview(item.previewUrl)
      if (item) this.$delete(this.deferredImagePending, key)
    },
    deferredImageClearAll() {
      const keys = Object.keys(this.deferredImagePending || {})
      keys.forEach((key) => this.deferredImageRevoke(key))
    },
    async deferredImageEnsure(key, options) {
      const item = this.deferredImagePending[key]
      const foo = (options && options.foo) || ''
      const endpoint = (options && options.endpoint) || 'Pic'
      const currentUrl = (options && options.currentUrl) || (item && item.savedUrl) || ''

      if (item && item.file) {
        // HeadPic 第 4 参为 category 字符串；Pic 第 4 参为 options（noOss 等）
        const uploaded = endpoint === 'HeadPic'
          ? await uploadHeadPic(item.file, this, foo, options && options.category)
          : await uploadPic(item.file, this, foo, options)
        this.deferredImageRevoke(key)
        return uploaded.url
      }
      if (!currentUrl) return ''
      if (String(currentUrl).indexOf('blob:') === 0) return ''
      // 剥掉 OSS 预签名参数，防止把临时 ?Expires=...&Signature=... 写入数据库
      return stripOssPresignParams(currentUrl)
    },
  /**
   * picture-card 多图：fileList 项含 raw 为待上传，提交时批量上传。
   * @returns {Promise<string[]>} 服务器路径列表
   */
    async deferredPictureCardEnsure(fileList, foo) {
      const list = fileList || []
      const urls = []
      for (let i = 0; i < list.length; i++) {
        const item = list[i]
        if (item.raw) {
          const { url } = await uploadPic(item.raw, this, foo)
          urls.push(url)
        } else {
          const path = this.deferredPictureCardItemPath(item)
          if (path) urls.push(path)
        }
      }
      return urls
    },
    deferredPictureCardItemPath(item) {
      if (!item || !item.url) return ''
      const url = String(item.url)
      if (url.indexOf('blob:') === 0 || url.indexOf('data:') === 0) return ''
      if (/^https?:\/\//i.test(url)) {
        // OSS 地址：剥掉预签名参数，保留完整 https://bucket.oss-region.aliyuncs.com/key
        // 非 OSS HTTPS：同样只取 origin+pathname（去掉 query）
        return stripOssPresignParams(url)
      }
      return url
    },
    /** picture-card on-change：auto-upload=false 时维护展示列表 */
    deferredPictureCardChange(file, fileList, targetListKey) {
      const mapped = (fileList || []).map((f) => {
        if (f.raw && (!f.url || f.url.indexOf('blob:') !== 0)) {
          return Object.assign({}, f, { url: URL.createObjectURL(f.raw), status: 'success' })
        }
        return f
      })
      this[targetListKey] = mapped
    },
    deferredPictureCardRemove(file, fileList, targetListKey) {
      if (file && file.url && String(file.url).indexOf('blob:') === 0) {
        revokeLocalAvatarPreview(file.url)
      }
      this[targetListKey] = fileList || []
    },
    deferredPictureCardClear(targetListKey) {
      const list = this[targetListKey] || []
      list.forEach((f) => {
        if (f.url && String(f.url).indexOf('blob:') === 0) revokeLocalAvatarPreview(f.url)
      })
      this[targetListKey] = []
    },
    /**
     * 编辑回填：将 MediaFiles 等路径转为 el-upload picture-card 的 fileList。
     * OSS 私有桶会走预签（含列表预取缓存）；本机路径拼 BaseApiUrl。
     * 保存时 deferredPictureCardItemPath 会剥掉 Expires 等签名参数。
     */
    async deferredPictureCardFromMedia(media, options) {
      const paths = typeof this.showImage === 'function'
        ? this.showImage(media)
        : (Array.isArray(media) ? media : String(media || '').split(/[;,|]/).map((s) => s.trim()).filter(Boolean))
      if (!paths.length) return []

      const schoolId = (options && options.schoolId) || getUploadSchoolId(this)
      const ossPaths = paths.filter((p) => isAliyunOssHttpsUrl(stripOssUrlQuery(p)))
      if (ossPaths.length && schoolId) {
        try {
          await batchResolveOssMediaUrls(ossPaths, schoolId, { inline: true })
        } catch (e) {
          console.warn('[deferredPictureCardFromMedia]', e)
        }
      }

      return paths.map((raw, idx) => {
        const stable = stripOssUrlQuery(raw)
        let display = resolveMediaUrl(stable)
        if (isAliyunOssHttpsUrl(stable) && schoolId) {
          display = lookupCachedOssSignedUrl(schoolId, stable, true) || display
        }
        const name = (stable.split('/').pop() || ('img' + idx)).split('?')[0]
        return {
          name,
          status: 'success',
          uid: Date.now() + '_' + idx,
          url: display
        }
      })
    }
  }
}
